import {
  Download,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, type Theme, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import {
  FOCUS_MODE_BACKGROUNDS,
  FOCUS_MODE_BORDERS,
  FOCUS_MODE_GRADIENTS,
  FOCUS_MODE_SHADOWS,
  getFocusModeValue,
} from '@src/constants/design-tokens';
import { COLORS } from '@src/constants/design-tokens/colors';
import { useControlAreaNavigation } from '@src/hooks/useControlAreaNavigation';
import { useControlAreaScroll } from '@src/hooks/useControlAreaScroll';
import { useControlAreaStyles } from '@src/hooks/useControlAreaStyles';
import { usePdfDownload } from '@src/hooks/usePdfDownload';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { memo, type ReactElement, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

type MobileControlAreaProps = {
  isScrolling?: boolean;
  isInputActive?: boolean;
  page?: number;
  pageTotal?: number;
  onPageChange?: (page: number) => void;
  onDownload?: () => void;
  isPreviewMode?: boolean;
  isPreviewGenerating?: boolean;
  onPreviewToggle?: () => void;
};

const MobileControlArea = memo(
  ({
    isScrolling = false,
    isInputActive: externalIsInputActive = false,
    page = 1,
    pageTotal = 1,
    onPageChange,
    onDownload,
    isPreviewMode = false,
    isPreviewGenerating = false,
    onPreviewToggle,
  }: MobileControlAreaProps): ReactElement | null => {
    const location = useLocation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isInputActive = externalIsInputActive;
    const isFocusMode = useSelector(selectFocusMode) || false;
    const isDarkSurface = isFocusMode || isDarkMode;
    const isRirekishoPage = location.pathname.startsWith('/rirekisho');

    // 共通フックを使用
    const { isPdfGenerating, handlePdfDownload } = usePdfDownload({
      toastMessages: {
        pending: '経歴書をダウンロードしています',
        success: '経歴書のダウンロードが完了しました',
        error: '経歴書のダウンロードに失敗しました',
      },
      onDownload,
    });

    const currentPageInfo = useControlAreaNavigation(isRirekishoPage ? 'rirekisho' : 'resume');
    const { handlePrevPage: handlePrevInputPage, handleNextPage: handleNextInputPage } =
      useControlAreaScroll(currentPageInfo);
    const { buttonStyles } = useControlAreaStyles({
      isFocusMode,
      isScrolling,
      isInputActive,
      isInline: false,
      contentType: isRirekishoPage ? 'rirekisho' : 'resume',
    });

    const canUsePdfNavigation = isPreviewMode && typeof onPageChange === 'function';

    const handlePrevButton = useCallback((): void => {
      if (canUsePdfNavigation && page > 1) {
        onPageChange?.(page - 1);
        return;
      }
      handlePrevInputPage();
    }, [canUsePdfNavigation, page, onPageChange, handlePrevInputPage]);

    const handleNextButton = useCallback((): void => {
      if (canUsePdfNavigation && page < pageTotal) {
        onPageChange?.(page + 1);
        return;
      }
      handleNextInputPage();
    }, [canUsePdfNavigation, page, pageTotal, onPageChange, handleNextInputPage]);

    const tooltipConfig = useMemo(
      () => ({
        arrow: true,
        placement: 'top' as const,
        enterDelay: 150,
        enterNextDelay: 150,
        disableTouchListener: true,
        componentsProps: {
          popper: {
            sx: (t: Theme) => ({ zIndex: t.zIndex.modal + 10100 }),
          },
        },
      }),
      []
    );

    const containerSx = useMemo(
      () => ({
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        left: '50%',
        zIndex: (t: Theme) => (isPreviewMode ? t.zIndex.modal + 10050 : 9999),
        backgroundColor: getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'primary', isDarkSurface),
        backdropFilter: 'blur(12px)',
        borderRadius: { xs: 2, sm: 3 },
        boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'primary', isDarkSurface),
        border: `2px solid ${getFocusModeValue(FOCUS_MODE_BORDERS, 'primary', isDarkSurface)}`,
        padding: { xs: '4px 6px', sm: '8px 10px' },
        maxWidth: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
        display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' },
        overflow: 'hidden', // 進捗バーのために追加
        opacity: isScrolling || isInputActive ? 0.4 : 1,
        transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        filter: isScrolling || isInputActive ? 'blur(0.5px)' : 'none',
        transform:
          isScrolling || isInputActive ? 'translateX(-50%) scale(0.95)' : 'translateX(-50%)',
        '&:hover': {
          opacity: 1,
          transform: 'translateX(-50%) translateY(-4px) scale(1.02)',
          boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'hover', isDarkSurface),
          backgroundColor: getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'darker', isDarkSurface),
          filter: 'none',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -1,
          left: -1,
          right: -1,
          bottom: -1,
          background: getFocusModeValue(FOCUS_MODE_GRADIENTS, 'before', isDarkSurface),
          borderRadius: 'inherit',
          zIndex: -1,
          opacity: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover::before': { opacity: 1 },
      }),
      [isDarkSurface, isScrolling, isInputActive, isPreviewMode]
    );

    const actionButtonBaseSx = useMemo(
      () => ({
        minWidth: { xs: 52, sm: 56 },
        minHeight: { xs: 52, sm: 56 },
        borderRadius: 999,
        padding: 0,
        '& svg': { fontSize: '1.35rem' },
      }),
      []
    );

    const previewButtonSx = useMemo(
      () => ({
        ...buttonStyles.primary,
        ...actionButtonBaseSx,
        ...(isPreviewMode && {
          backgroundColor: isDarkSurface
            ? alpha(COLORS.primary.light, 0.3)
            : alpha(COLORS.primary.light, 0.15),
          '&:hover': {
            backgroundColor: isDarkSurface
              ? alpha(COLORS.primary.light, 0.4)
              : alpha(COLORS.primary.light, 0.25),
          },
        }),
        ...(isPreviewGenerating && {
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.6 },
            '100%': { opacity: 1 },
          },
        }),
      }),
      [buttonStyles.primary, isPreviewMode, isDarkSurface, isPreviewGenerating, actionButtonBaseSx]
    );

    const downloadButtonSx = useMemo(
      () => ({
        ...buttonStyles.download,
        ...actionButtonBaseSx,
        ...(isPdfGenerating && {
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.05)', opacity: 0.8 },
            '100%': { transform: 'scale(1)', opacity: 1 },
          },
        }),
      }),
      [buttonStyles.download, isPdfGenerating, actionButtonBaseSx]
    );

    if (!currentPageInfo) {
      return null;
    }

    const prevLabel = canUsePdfNavigation ? '前のPDFページ' : '前の入力ページ';
    const nextLabel = canUsePdfNavigation ? '次のPDFページ' : '次の入力ページ';
    const isPrevDisabled = canUsePdfNavigation
      ? page <= 1 || pageTotal <= 0
      : isPreviewMode || currentPageInfo.isFirst;
    const isNextDisabled = canUsePdfNavigation
      ? page >= pageTotal || pageTotal <= 0
      : isPreviewMode || currentPageInfo.isLast;

    const progressColor = isDarkSurface
      ? alpha(COLORS.darkMode.text.primary, 0.7)
      : alpha(COLORS.text.primary, 0.6);

    // ページ進捗率の計算
    const rawProgress = canUsePdfNavigation
      ? (page / pageTotal) * 100
      : currentPageInfo.totalPages > 0
        ? ((currentPageInfo.currentIndex + 1) / currentPageInfo.totalPages) * 100
        : 0;
    const pageProgress = Number.isFinite(rawProgress) ? rawProgress : 0;
    const progressMainColor = isDarkSurface ? COLORS.primary['300'] : COLORS.primary.main;

    return (
      <Box role="toolbar" aria-label="モバイル操作コントロール" sx={containerSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: (t: Theme) => t.spacing(0.75),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: (t: Theme) => t.spacing(0.125),
              opacity: !canUsePdfNavigation && isPreviewMode ? 0.5 : 1,
            }}
          >
            <Tooltip {...tooltipConfig} title={prevLabel}>
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  onClick={handlePrevButton}
                  size="small"
                  aria-label={prevLabel}
                  disabled={isPrevDisabled}
                  sx={{ ...buttonStyles.primary, ...actionButtonBaseSx }}
                >
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip {...tooltipConfig} title={nextLabel}>
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  onClick={handleNextButton}
                  size="small"
                  aria-label={nextLabel}
                  disabled={isNextDisabled}
                  sx={{ ...buttonStyles.primary, ...actionButtonBaseSx }}
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: (t: Theme) => t.spacing(0.125) }}>
            <Tooltip
              {...tooltipConfig}
              title={
                isPreviewGenerating
                  ? 'PDF生成中...'
                  : isPreviewMode
                    ? 'プレビューを閉じる'
                    : 'プレビューを開く'
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  onClick={onPreviewToggle}
                  size="small"
                  disabled={isPreviewGenerating}
                  aria-label={
                    isPreviewGenerating
                      ? 'PDF生成中'
                      : isPreviewMode
                        ? 'プレビューを閉じる'
                        : 'プレビューを開く'
                  }
                  sx={previewButtonSx}
                >
                  {isPreviewGenerating ? (
                    <CircularProgress size={16} sx={{ color: progressColor }} />
                  ) : isPreviewMode ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              {...tooltipConfig}
              title={isPdfGenerating ? 'ダウンロード中...' : 'PDFをダウンロード'}
            >
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  onClick={handlePdfDownload}
                  size="small"
                  aria-label={isPdfGenerating ? 'ダウンロード中' : 'PDFダウンロード'}
                  disabled={isPdfGenerating}
                  sx={downloadButtonSx}
                >
                  {isPdfGenerating ? (
                    <CircularProgress size={16} sx={{ color: progressColor }} />
                  ) : (
                    <Download fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* 現在位置を示す進捗バー */}
        <LinearProgress
          variant="determinate"
          value={pageProgress}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: 3,
            bgcolor: alpha(progressMainColor, 0.1),
            '& .MuiLinearProgress-bar': {
              bgcolor: progressMainColor,
              transition: 'transform 0.3s ease-out',
            },
          }}
        />
      </Box>
    );
  }
);

MobileControlArea.displayName = 'MobileControlArea';

export default MobileControlArea;
